import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Loader2, Link2, Copy, Check, Users, MousePointerClick, DollarSign, UserPlus, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function Affiliate() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [affiliate, setAffiliate] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [signingUp, setSigningUp] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const [updatingSettings, setUpdatingSettings] = useState(false);

  useEffect(() => {
    fetchAffiliateStatus();
  }, []);

  const fetchAffiliateStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/affiliate/status');
      setIsAffiliate(response.data.is_affiliate);
      if (response.data.is_affiliate) {
        setAffiliate(response.data.affiliate);
        setReferrals(response.data.referrals || []);
        setPaypalEmail(response.data.affiliate.paypal_email || '');
      }
    } catch (error) {
      console.error('Failed to fetch affiliate status:', error);
      toast.error('Failed to load affiliate status');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    try {
      setSigningUp(true);
      const response = await api.post('/affiliate/signup', { paypal_email: paypalEmail || null });
      if (response.data.success) {
        toast.success('Welcome to the affiliate program!');
        setIsAffiliate(true);
        setAffiliate(response.data.affiliate);
      }
    } catch (error) {
      console.error('Failed to sign up:', error);
      toast.error('Failed to sign up as affiliate');
    } finally {
      setSigningUp(false);
    }
  };

  const handleUpdateSettings = async () => {
    try {
      setUpdatingSettings(true);
      await api.put('/affiliate/settings', { paypal_email: paypalEmail });
      toast.success('Settings updated');
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setUpdatingSettings(false);
    }
  };

  const getAffiliateLink = () => {
    if (!affiliate) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/api/ref/${affiliate.code}`;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(getAffiliateLink());
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <div className="flex items-center gap-3 mb-8">
        <Link2 className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">Affiliate Program</h1>
          <p className="text-muted-foreground">Earn commissions by referring new users</p>
        </div>
      </div>

      {!isAffiliate ? (
        <Card>
          <CardHeader>
            <CardTitle>Join Our Affiliate Program</CardTitle>
            <CardDescription>
              Earn 10% commission on every purchase made by users you refer
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="p-4 bg-secondary/30 rounded-lg text-center">
                <Link2 className="h-8 w-8 mx-auto mb-2 text-primary" />
                <h3 className="font-semibold">Get Your Link</h3>
                <p className="text-sm text-muted-foreground">Generate a unique referral link</p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-lg text-center">
                <Users className="h-8 w-8 mx-auto mb-2 text-primary" />
                <h3 className="font-semibold">Share & Refer</h3>
                <p className="text-sm text-muted-foreground">Share with your audience</p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-lg text-center">
                <DollarSign className="h-8 w-8 mx-auto mb-2 text-primary" />
                <h3 className="font-semibold">Earn 10%</h3>
                <p className="text-sm text-muted-foreground">Get paid for conversions</p>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <div>
                <Label htmlFor="paypal">PayPal Email (for payouts)</Label>
                <Input
                  id="paypal"
                  type="email"
                  placeholder="your@paypal.com"
                  value={paypalEmail}
                  onChange={(e) => setPaypalEmail(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">Optional - you can add this later</p>
              </div>

              <Button onClick={handleSignup} disabled={signingUp} size="lg" className="w-full">
                {signingUp ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Signing up...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Become an Affiliate
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Your Affiliate Link</CardTitle>
              <CardDescription>Share this link to earn commissions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  value={getAffiliateLink()}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button onClick={copyLink} variant="outline">
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open(getAffiliateLink(), '_blank')}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Affiliate Code: <span className="font-mono font-semibold">{affiliate.code}</span>
              </p>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <MousePointerClick className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{affiliate.clicks}</p>
                    <p className="text-sm text-muted-foreground">Clicks</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/10 rounded-lg">
                    <UserPlus className="h-5 w-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{affiliate.signups}</p>
                    <p className="text-sm text-muted-foreground">Signups</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Users className="h-5 w-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{affiliate.conversions}</p>
                    <p className="text-sm text-muted-foreground">Conversions</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-500/10 rounded-lg">
                    <DollarSign className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">${affiliate.earnings.toFixed(2)}</p>
                    <p className="text-sm text-muted-foreground">Earnings</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Recent Referrals</CardTitle>
              <CardDescription>Users who signed up through your link</CardDescription>
            </CardHeader>
            <CardContent>
              {referrals.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No referrals yet</p>
                  <p className="text-sm">Share your link to start earning</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referrals.map((ref) => (
                      <TableRow key={ref.id}>
                        <TableCell>{formatDate(ref.created_at)}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            ref.converted 
                              ? 'bg-green-500/10 text-green-500' 
                              : 'bg-yellow-500/10 text-yellow-500'
                          }`}>
                            {ref.converted ? 'Converted' : 'Signed Up'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {ref.conversion_amount ? `$${(ref.conversion_amount * 0.1).toFixed(2)}` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Payout Settings</CardTitle>
              <CardDescription>Configure how you receive your earnings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="paypal-settings">PayPal Email</Label>
                  <Input
                    id="paypal-settings"
                    type="email"
                    placeholder="your@paypal.com"
                    value={paypalEmail}
                    onChange={(e) => setPaypalEmail(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <Button 
                  onClick={handleUpdateSettings} 
                  disabled={updatingSettings}
                >
                  {updatingSettings ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Settings'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
